import { collectAssemblyNodeIdentityLookupIds } from '../../assembly/utils/AssemblyNodePath'
import {
  collectAssemblyDocumentNodeIdentityLookupIds,
  isAssemblyPersistedDocument,
} from '../../project-model/assembly-document/assembly-document-operations'
import type {
  AssemblyDocument,
  AssemblyDocumentNode,
  AssemblyIndexedDocument,
  AssemblyPersistedChildNode,
  AssemblyTransform,
} from '../../project-model/assembly-document/assembly-document-types'
import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyProjectModelDocument,
  PuppyProjectModelOccurrence,
  PuppyProjectModelVersionReference,
} from './types'
import { PUPPY_PROJECT_MODEL_VERSION } from './types'

/**
 * v4 外层装配文档中的单个项目 occurrence 引用。
 *
 * 该结构只存在于迁移过程，不进入离线包协议。它把后端项目身份与包内 assemblyNodeId
 * 的匹配职责集中在兼容适配器中，避免加载器和 UI 各自猜测节点身份。
 */
interface LegacyProjectOccurrenceReference {
  /** 迁移过程内的稳定引用 ID。 */
  readonly referenceId: string
  /** 父引用 ID；根的直接子节点指向 `root`。 */
  readonly parentReferenceId: string
  /** 项目 occurrence 显示名称。 */
  readonly name: string
  /** 项目节点类型。 */
  readonly nodeType: PuppyProjectModelOccurrence['nodeType']
  /** 后端持久化节点 ID。 */
  readonly persistedNodeId?: string
  /** 后端 occurrence 实例 ID。 */
  readonly instanceId?: string
  /** 可选版本引用。 */
  readonly versionReference?: PuppyProjectModelVersionReference
  /** 与历史包节点做严格交集匹配的归一化身份集合。 */
  readonly identityLookupIds: ReadonlySet<string>
}

/**
 * 判断装配工作文档是否是旧的索引形态。
 *
 * @param document 待判断的装配工作文档。
 * @returns 文档携带 `schemaVersion` 时返回 true。
 */
function isIndexedAssemblyDocument(document: AssemblyDocument): document is AssemblyIndexedDocument {
  return 'schemaVersion' in document
}

/**
 * 从装配工作文档节点读取有效版本引用。
 *
 * @param node 装配工作文档节点。
 * @returns 仓库和提交均有效时返回版本引用，否则返回 undefined。
 */
function readIndexedVersionReference(
  node: AssemblyDocumentNode,
): PuppyProjectModelVersionReference | undefined {
  const repositoryId = node.repositoryId.trim()
  const commitId = node.commitId.trim()
  if (!repositoryId || !commitId) {
    return undefined
  }
  return {
    repositoryId,
    ...(node.branchId?.trim() ? { branchId: node.branchId.trim() } : {}),
    commitId,
  }
}

/**
 * 从完整后端节点的 runtime 读取有效版本引用。
 *
 * @param child 后端持久化装配子节点。
 * @returns runtime 完整时返回版本引用，否则返回 undefined。
 */
function readPersistedVersionReference(
  child: AssemblyPersistedChildNode,
): PuppyProjectModelVersionReference | undefined {
  const runtime = child.runtime
  if (!runtime || typeof runtime !== 'object' || Array.isArray(runtime)) {
    return undefined
  }
  const repositoryId = runtime.repository_id
  const branchId = runtime.branch_id
  const commitId = runtime.commit_id
  if (
    (typeof repositoryId !== 'string' && typeof repositoryId !== 'number')
    || String(repositoryId).trim().length === 0
    || typeof commitId !== 'string'
    || commitId.trim().length === 0
  ) {
    return undefined
  }
  return {
    repositoryId: String(repositoryId),
    ...(branchId !== undefined && String(branchId).trim().length > 0
      ? { branchId: String(branchId) }
      : {}),
    commitId: commitId.trim(),
  }
}

/**
 * 为完整后端节点创建统一身份摘要。
 *
 * @param child 后端持久化装配子节点。
 * @param referenceId 当前迁移引用 ID。
 * @returns 可复用公共身份归一化规则的装配节点摘要。
 */
function createPersistedAssemblyDocumentNode(
  child: AssemblyPersistedChildNode,
  referenceId: string,
): AssemblyDocumentNode {
  const versionReference = readPersistedVersionReference(child)
  return {
    nodeKey: referenceId,
    persistedNodeId: child.node_id,
    nodeType: child.node_type,
    repositoryId: versionReference?.repositoryId ?? '',
    ...(versionReference?.branchId ? { branchId: versionReference.branchId } : {}),
    commitId: versionReference?.commitId ?? '',
    transform: [...child.transform] as AssemblyTransform,
    instanceId: child.instance_id,
    instanceName: child.instance_name,
    nodeWhere: child.node_where,
    packageGlbEntry: child.package_glb_entry,
    nestedAssemblyJson: child.nested_assembly_json,
    refProductId: child.ref_product_id,
    ...(child.debug ? { debug: child.debug } : {}),
  }
}

/**
 * 展开索引形态装配文档的全部非根 occurrence。
 *
 * @param document 索引形态装配文档。
 * @returns 按项目树前序排列的迁移引用。
 */
function collectIndexedOccurrenceReferences(
  document: AssemblyIndexedDocument,
): LegacyProjectOccurrenceReference[] {
  const references: LegacyProjectOccurrenceReference[] = []
  const visit = (parentNodeKey: string, parentReferenceId: string): void => {
    const childNodeKeys = document.childrenByNodeKey[parentNodeKey] ?? []
    childNodeKeys.forEach((childNodeKey) => {
      const child = document.nodesByKey[childNodeKey]
      if (!child) {
        throw new Error(`v4 PuppyCAD 项目迁移缺少装配节点：${childNodeKey}`)
      }
      const versionReference = readIndexedVersionReference(child)
      references.push({
        referenceId: childNodeKey,
        parentReferenceId,
        name: child.instanceName?.trim() || childNodeKey,
        nodeType: child.nodeType,
        ...(child.persistedNodeId ? { persistedNodeId: child.persistedNodeId } : {}),
        ...(child.instanceId ? { instanceId: child.instanceId } : {}),
        ...(versionReference ? { versionReference } : {}),
        identityLookupIds: new Set(collectAssemblyDocumentNodeIdentityLookupIds(child)),
      })
      visit(childNodeKey, childNodeKey)
    })
  }

  visit(document.rootNodeKey, 'root')
  return references
}

/**
 * 展开完整后端装配文档的全部非根 occurrence。
 *
 * @param children 当前父节点的持久化子节点。
 * @param parentReferenceId 父迁移引用 ID。
 * @param target 引用输出数组。
 */
function collectPersistedOccurrenceReferences(
  children: readonly AssemblyPersistedChildNode[],
  parentReferenceId: string,
  target: LegacyProjectOccurrenceReference[],
): void {
  children.forEach((child, siblingIndex) => {
    const referenceId = `${parentReferenceId}/${encodeURIComponent(`${child.instance_name}#${siblingIndex}`)}`
    const documentNode = createPersistedAssemblyDocumentNode(child, referenceId)
    const versionReference = readPersistedVersionReference(child)
    target.push({
      referenceId,
      parentReferenceId,
      name: child.instance_name.trim() || child.node_id,
      nodeType: child.node_type,
      persistedNodeId: child.node_id,
      instanceId: child.instance_id,
      ...(versionReference ? { versionReference } : {}),
      identityLookupIds: new Set(collectAssemblyDocumentNodeIdentityLookupIds(documentNode)),
    })
    collectPersistedOccurrenceReferences(child.children ?? [], referenceId, target)
  })
}

/**
 * 收集装配工作文档中需要恢复的非根项目 occurrence。
 *
 * @param document 当前项目的 `_assembly.json` 文档。
 * @returns 按项目树前序排列的迁移引用。
 */
function collectLegacyOccurrenceReferences(
  document: AssemblyDocument,
): LegacyProjectOccurrenceReference[] {
  if (isIndexedAssemblyDocument(document)) {
    return collectIndexedOccurrenceReferences(document)
  }
  if (!isAssemblyPersistedDocument(document)) {
    return []
  }
  const references: LegacyProjectOccurrenceReference[] = []
  collectPersistedOccurrenceReferences(document.children, 'root', references)
  return references
}

/** 历史 `.puppy/assembly.json` 中可能保存节点身份的字段。 */
const LEGACY_PACKAGE_IDENTITY_FIELDS = [
  'assemblyNodeId',
  'selectionObjectId',
  'sourceAssemblyNodeId',
  'componentInstanceId',
  'nodeId',
  'node_id',
  'refProductId',
  'ref_product_id',
] as const

/**
 * 从历史包节点的 metadata/extensions 中收集已知身份字段。
 *
 * @param value metadata 或 extensions 值。
 * @returns 归一化后的身份 token。
 */
function collectLegacyRecordIdentityLookupIds(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return []
  }
  const record = value as Record<string, unknown>
  return LEGACY_PACKAGE_IDENTITY_FIELDS.flatMap((field) => (
    collectAssemblyNodeIdentityLookupIds(record[field])
  ))
}

/**
 * 收集历史包装配节点的全部稳定身份。
 *
 * @param node `.puppy/assembly.json` 节点。
 * @returns 去重后的归一化身份集合。
 */
function collectPackageNodeIdentityLookupIds(node: PuppyAssemblyNode): ReadonlySet<string> {
  return new Set([
    ...collectAssemblyNodeIdentityLookupIds(node.nodeId),
    ...collectAssemblyNodeIdentityLookupIds(node.name),
    ...collectAssemblyNodeIdentityLookupIds(node.path),
    ...collectLegacyRecordIdentityLookupIds(node.meta),
    ...collectLegacyRecordIdentityLookupIds(node.extensions),
  ])
}

/**
 * 判断包节点是否与项目 occurrence 引用共享稳定身份。
 *
 * @param packageLookupIds 包节点身份集合。
 * @param referenceLookupIds 项目引用身份集合。
 * @returns 两个集合存在交集时返回 true。
 */
function hasIdentityIntersection(
  packageLookupIds: ReadonlySet<string>,
  referenceLookupIds: ReadonlySet<string>,
): boolean {
  return [...packageLookupIds].some((identity) => referenceLookupIds.has(identity))
}

/**
 * 判断候选装配节点是否位于指定父装配节点的子树内。
 *
 * @param childNodeId 候选子节点 ID。
 * @param parentNodeId 期望父项目 occurrence 对应的装配节点 ID。
 * @param nodeById 包内装配节点索引。
 * @returns 候选节点向上可达父节点时返回 true。
 */
function isAssemblyNodeDescendantOf(
  childNodeId: string,
  parentNodeId: string,
  nodeById: ReadonlyMap<string, PuppyAssemblyNode>,
): boolean {
  let current = nodeById.get(childNodeId)
  const visitedNodeIds = new Set<string>()
  while (current?.parentNodeId) {
    if (current.parentNodeId === parentNodeId) {
      return true
    }
    if (visitedNodeIds.has(current.parentNodeId)) {
      return false
    }
    visitedNodeIds.add(current.parentNodeId)
    current = nodeById.get(current.parentNodeId)
  }
  return false
}

/**
 * 将没有 `project-model.json` 的 v4 离线包严格迁移为项目 occurrence 文档。
 *
 * 迁移只接受“外层 `_assembly.json` occurrence 与包内 assembly 节点一一对应、且父子作用域一致”
 * 的输入。任何缺失或歧义都会返回 null，由原有后端补树兼容链路继续处理；这里绝不按名称任选节点。
 *
 * @deprecated v4 occurrence 迁移兼容计划于 2026-08-11 移除。
 *
 * @param input v4 包装配语义与当前项目装配工作文档。
 * @returns 可安全重建项目树的 occurrence 文档；无法证明唯一性时返回 null。
 */
export function migrateLegacyPuppyProjectModelDocument(input: {
  readonly assembly: PuppyAssemblyDocument
  readonly assemblyDocument: AssemblyDocument
}): PuppyProjectModelDocument | null {
  const nodeById = new Map(input.assembly.nodes.map((node) => [node.nodeId, node]))
  const rootAssemblyNode = nodeById.get(input.assembly.rootNodeId)
  if (!rootAssemblyNode) {
    return null
  }

  const references = collectLegacyOccurrenceReferences(input.assemblyDocument)
  const packageCandidates = input.assembly.nodes.filter((node) => (
    node.nodeId !== input.assembly.rootNodeId
    && (node.semanticKind === 'assembly' || node.semanticKind === 'part')
  ))
  const packageLookupIdsByNodeId = new Map(
    packageCandidates.map((node) => [node.nodeId, collectPackageNodeIdentityLookupIds(node)]),
  )
  const assemblyNodeIdByReferenceId = new Map<string, string>([
    ['root', input.assembly.rootNodeId],
  ])
  const usedAssemblyNodeIds = new Set<string>([input.assembly.rootNodeId])
  const occurrences: PuppyProjectModelOccurrence[] = [{
    occurrenceId: 'root',
    parentOccurrenceId: null,
    assemblyNodeId: input.assembly.rootNodeId,
    name: rootAssemblyNode.name,
    nodeType: rootAssemblyNode.semanticKind === 'part' ? 'part' : 'assembly',
  }]

  for (const reference of references) {
    const parentAssemblyNodeId = assemblyNodeIdByReferenceId.get(reference.parentReferenceId)
    if (!parentAssemblyNodeId) {
      return null
    }
    const candidates = packageCandidates.filter((candidate) => (
      !usedAssemblyNodeIds.has(candidate.nodeId)
      && isAssemblyNodeDescendantOf(candidate.nodeId, parentAssemblyNodeId, nodeById)
      && hasIdentityIntersection(
        packageLookupIdsByNodeId.get(candidate.nodeId) ?? new Set<string>(),
        reference.identityLookupIds,
      )
    ))
    if (candidates.length !== 1) {
      return null
    }

    const candidate = candidates[0]!
    const occurrenceId = `${reference.parentReferenceId}/${encodeURIComponent(candidate.nodeId)}`
    assemblyNodeIdByReferenceId.set(reference.referenceId, candidate.nodeId)
    usedAssemblyNodeIds.add(candidate.nodeId)
    occurrences.push({
      occurrenceId,
      parentOccurrenceId: reference.parentReferenceId === 'root'
        ? 'root'
        : occurrences.find((occurrence) => (
            occurrence.assemblyNodeId === parentAssemblyNodeId
          ))?.occurrenceId ?? null,
      assemblyNodeId: candidate.nodeId,
      ...(reference.persistedNodeId ? { persistedNodeId: reference.persistedNodeId } : {}),
      ...(reference.instanceId ? { instanceId: reference.instanceId } : {}),
      name: reference.name,
      nodeType: reference.nodeType,
      ...(reference.versionReference ? { versionReference: reference.versionReference } : {}),
    })
    if (occurrences.at(-1)?.parentOccurrenceId === null) {
      return null
    }
  }

  return {
    version: PUPPY_PROJECT_MODEL_VERSION,
    rootOccurrenceId: 'root',
    occurrences,
  }
}
